import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo, useState } from "react"
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router"
import { PageScroll, PeopleDirectory, PhotoDirectory } from "@/design-system/index.ts"
import { peopleQuery } from "@/people/client.ts"
import { SidebarInset, SidebarProvider } from "@/primitives/sidebar.tsx"
import { photosQuery } from "@/profile/client.ts"
import { checkShows } from "@/profile/describe.ts"
import { ProfileCheck } from "@/profile/profile-check.tsx"
import { myTicketsQuery, projectsQuery } from "@/projects/client.ts"
import { SignInScreen } from "@/sign-in/sign-in-screen.tsx"
import { meQuery, signOut, type Me } from "@/sign-in/client.ts"
import { useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { AppSidebar } from "./app-sidebar.tsx"
import { CommandPalette, CommandPaletteContext } from "./command-palette.tsx"
import { adminDestinations, destinations } from "./destinations.ts"
import { ShellError } from "./route-error.tsx"
import { ShellSkeleton } from "./shell-skeleton.tsx"
import { ShortcutsDialog } from "./shortcuts-dialog.tsx"

/*
 * The shell every page renders in, and the guard in front of it. Beacon first asks who is signed
 * in: while it asks, the shell's empty frame; if nobody is, the sign-in card, at whatever address
 * was opened, and back to it afterwards; once someone is, the shell.
 *
 * The shell is the sidebar on the canvas and, beside it, the inset panel that is the page: one
 * scroll container, in which each page renders its own PageHeader first. The command palette, the
 * Shortcuts dialog and the keys that open them and move between pages live here, for every page,
 * and so do every Member's photos, which each avatar reads, and the once-only check of the signed-in
 * Member's seeded Profile.
 */

/** Who is signed in. Every page under the shell has someone; the guard shows no page otherwise. */
export type Session = { status: "signed-in"; me: Me }

type LayoutContext = { session: Session; signOut: () => Promise<void> }

/** Who is signed in, as the shell found out once for every page under it. */
export function useSession() {
  return useOutletContext<LayoutContext>()
}

// The sidebar opens with the page only where the widest table still fits beside it: 15rem of
// sidebar and a 56rem table need 1280px. Narrower than that it starts collapsed to its icons, so a
// table scrolls only below the web app's 1024px minimum, and it can still be opened by hand.
const opensExpanded = () => window.matchMedia("(min-width: 1280px)").matches

export function Layout() {
  const me = useQuery(meQuery)
  const { pathname, search, hash } = useLocation()

  if (me.isPending) return <ShellSkeleton expanded={opensExpanded()} />
  if (me.isError) return <ShellError onRetry={() => void me.refetch()} />
  if (!me.data) return <SignInScreen next={`${pathname}${search}${hash}`} />
  return <Shell me={me.data} />
}

/**
 * The G-chords: G then a letter goes to a page. The Admin pages' chords are bound only for an
 * Admin, as only an Admin has those pages.
 */
function useGoToChords(admin: boolean) {
  const navigate = useNavigate()
  const chords = (list: typeof destinations) =>
    Object.fromEntries(list.map(({ to, chord }) => [chord, () => void navigate(to)]))
  useShortcuts(chords(destinations))
  useShortcuts(chords(adminDestinations), { enabled: admin })
}

/** No photos, kept as one object so the directory doesn't change while they load. */
const noPhotos: Readonly<Record<string, string>> = {}

function Shell({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const admin = me.role === "admin"
  const projects = useQuery(projectsQuery(false)).data ?? []
  const openTickets = useQuery({
    ...myTicketsQuery(false, false),
    select: (groups) => groups.reduce((count, { tickets }) => count + tickets.length, 0),
  }).data
  const photos = useQuery(photosQuery).data ?? noPhotos
  const people = useQuery(peopleQuery).data
  const cards = useMemo(
    () => Object.fromEntries((people ?? []).map((person) => [person.id, person])),
    [people],
  )
  // How far each person's time is from yours: by your Profile's time zone, else this browser's.
  const viewerZone =
    people?.find(({ id }) => id === me.id)?.timeZone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone

  // Everything cached was read as the Member signing out, so none of it outlives their session:
  // resetting drops it all and asks again, which finds nobody signed in, and the guard shows the
  // sign-in card at the root.
  const signOutHere = useCallback(async () => {
    await signOut()
    await navigate("/")
    await queryClient.resetQueries()
  }, [navigate, queryClient])

  const openPalette = useCallback(() => setPaletteOpen(true), [])
  const showShortcuts = useCallback(() => setShortcutsOpen(true), [])
  const palette = useMemo(() => ({ openPalette }), [openPalette])
  const outlet = useMemo(
    () => ({ session: { status: "signed-in", me }, signOut: signOutHere }) satisfies LayoutContext,
    [me, signOutHere],
  )

  // ⌘B is the sidebar primitive's own; binding it here too would toggle twice. `/` is the page's
  // when it has a search field, so the shell binds it as a fallback: a fresh load mounts the page
  // first, and mounting later must not win it. Elsewhere it opens the palette.
  useShortcuts({
    "command-palette": openPalette,
    shortcuts: showShortcuts,
  })
  useShortcuts({ search: openPalette }, { fallback: true })
  useGoToChords(admin)

  return (
    <PhotoDirectory photos={photos}>
      <PeopleDirectory cards={cards} viewerZone={viewerZone}>
        <CommandPaletteContext value={palette}>
          <SidebarProvider defaultOpen={opensExpanded()}>
            <AppSidebar
              me={me}
              projects={projects}
              openTickets={openTickets}
              onSearch={openPalette}
              onShowShortcuts={showShortcuts}
              onSignOut={() => void signOutHere()}
            />
            <SidebarInset>
              <PageScroll>
                <Outlet context={outlet} />
              </PageScroll>
            </SidebarInset>
            <CommandPalette
              open={paletteOpen}
              onOpenChange={setPaletteOpen}
              admin={admin}
              projects={projects}
              onShowShortcuts={showShortcuts}
              onSignOut={() => void signOutHere()}
            />
            <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} admin={admin} />
            {checkShows(me) && <ProfileCheck me={me} />}
          </SidebarProvider>
        </CommandPaletteContext>
      </PeopleDirectory>
    </PhotoDirectory>
  )
}
